import { useEffect } from 'react';

import routeMeta from '../../data/routeMeta.json';

/**
 * Keeps the head correct after a client-side navigation, where no new document
 * is fetched and the prerendered tags still describe the previous page.
 *
 * Values come from the same src/data/routeMeta.json that
 * scripts/prerender-routes.js writes into each static file, so the two can
 * never disagree.
 *
 * Written against the DOM rather than react-helmet-async on purpose. Both v2
 * and v3 of that library APPEND their tags here instead of replacing the ones
 * already in the document, which left two canonical links and two descriptions
 * on every page, and neither version updated the title on a route change. Two
 * canonicals is worse than a stale one: search engines may discard both.
 * Updating the existing element in place guarantees exactly one of each.
 */

const upsertMeta = (attribute, key, content) => {
  let el = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const RouteHead = ({ path }) => {
  useEffect(() => {
    const route = routeMeta.routes.find((entry) => entry.path === path);
    if (!route) return;

    const url = `${routeMeta.siteUrl}${route.path}`;
    const image = `${routeMeta.siteUrl}${route.image || routeMeta.defaultImage}`;

    document.title = route.title;
    upsertMeta('name', 'description', route.description);
    upsertMeta('property', 'og:title', route.title);
    upsertMeta('property', 'og:description', route.description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('name', 'twitter:title', route.title);
    upsertMeta('name', 'twitter:description', route.description);
    upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:image', image);
    upsertCanonical(url);
  }, [path]);

  return null;
};

export default RouteHead;
